import { useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import { useSearch, SearchResult } from '../../hooks/useSearch';

interface SearchBoxProps {
  app: string | null;
  placeholder: string;
  onResultClick: (result: SearchResult) => void;
}

export default function SearchBox({ app, placeholder, onResultClick }: SearchBoxProps) {
  const { query, setQuery, results, loading, isOpen, setIsOpen } = useSearch(app);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setIsOpen]);

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2.5 bg-surface-light rounded-lg py-2.5 px-3">
        <Search size={16} className="text-content-secondary flex-shrink-0" />
        <input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0) setIsOpen(true); }}
          className="border-none bg-transparent outline-none flex-1 text-[13px] text-content-primary placeholder:text-content-secondary"
        />
        {loading && (
          <div className="w-4 h-4 border-2 border-content-border border-t-oak-green rounded-full animate-spin" />
        )}
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg border border-content-border shadow-lg max-h-80 overflow-auto z-50">
          {groupBySection(results).map(([sectionTitle, items]) => (
            <div key={sectionTitle}>
              <div className="px-3 py-2 text-xs font-semibold text-content-secondary bg-surface-light border-b border-content-border">
                {sectionTitle}
              </div>
              {items.map((result) => (
                <button
                  key={result.id}
                  onClick={() => {
                    onResultClick(result);
                    setIsOpen(false);
                    setQuery('');
                  }}
                  className="w-full text-left px-3 py-2.5 hover:bg-surface-light transition-colors border-b border-content-border last:border-b-0"
                >
                  <div className="text-sm font-medium text-content-primary">{result.title}</div>
                  {result.headline && (
                    <div
                      className="text-xs text-content-secondary mt-0.5 line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: result.headline.replace(/\*\*/g, '') }}
                    />
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function groupBySection(results: SearchResult[]): [string, SearchResult[]][] {
  const map = new Map<string, SearchResult[]>();
  for (const r of results) {
    const key = r.section_title || 'Other';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(r);
  }
  return Array.from(map.entries());
}
